import React, { useState, useEffect } from 'react';
import QuickActions from './QuickActions';
import RecentActivity from '../../user-account-dashboard/components/RecentActivity';
import { supabase } from '../../../lib/supabase';

const OverviewTab = ({ setActiveTab, setInitialComponentId }) => {
  const [mismatchedAlerts, setMismatchedAlerts] = useState([]);

  useEffect(() => {
    const loadNotificationAlerts = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        // Fetch seller account ID
        const { data: sellerAcc } = await supabase
          .from('seller_accounts')
          .select('id')
          .eq('user_id', user.id)
          .maybeSingle();

        if (!sellerAcc) return;

        // Query active offerings for this seller
        const { data: sellerComps } = await supabase
          .from('seller_components')
          .select(`
            id,
            custom_name,
            component_id,
            manufacturing_options(
              offered_methods,
              mfg_3d_types,
              mfg_3d_materials,
              mfg_cnc_types,
              mfg_cnc_materials
            ),
            component:components(
              id,
              name,
              is_3d_manufacturing,
              is_cnc_capable,
              mfg_3d_types,
              mfg_3d_materials,
              mfg_cnc_types,
              mfg_cnc_materials
            )
          `)
          .eq('seller_id', sellerAcc.id)
          .eq('status', 'active');

        if (sellerComps) {
          const alerts = [];
          sellerComps.forEach(sc => {
            const mfg = Array.isArray(sc.manufacturing_options) ? sc.manufacturing_options[0] : sc.manufacturing_options;
            const parent = sc.component;
            if (!mfg || !parent) return;

            const cncTypes = parent.mfg_cnc_types || [];
            const t3dTypes = parent.mfg_3d_types || [];
            const cncMats = parent.mfg_cnc_materials || [];
            const t3dMats = parent.mfg_3d_materials || [];

            let hasMismatch = false;
            if (mfg.offered_methods === '3d_only' || mfg.offered_methods === 'both') {
              const invalidTypes = mfg.mfg_3d_types?.some(t => !t3dTypes.includes(t));
              const invalidMats = mfg.mfg_3d_materials?.some(m => !t3dMats.includes(m));
              if (invalidTypes || invalidMats) hasMismatch = true;
            }
            if (mfg.offered_methods === 'cnc_only' || mfg.offered_methods === 'both') {
              const invalidTypes = mfg.mfg_cnc_types?.some(t => !cncTypes.includes(t));
              const invalidMats = mfg.mfg_cnc_materials?.some(m => !cncMats.includes(m));
              if (invalidTypes || invalidMats) hasMismatch = true;
            }

            if (hasMismatch) {
              alerts.push({
                id: sc.id,
                name: sc.custom_name || parent.name,
                globalId: parent.id
              });
            }
          });
          setMismatchedAlerts(alerts);
        }
      } catch (e) {
        console.error("Error loading notification alerts:", e);
      }
    };
    loadNotificationAlerts();
  }, []);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 text-left">
      {mismatchedAlerts.length > 0 && (
        <div className="bg-red-50 border border-red-200 text-red-900 rounded-xl p-5 shadow-sm space-y-4 col-span-1 lg:col-span-3 animate-in fade-in">
          <div className="flex items-center gap-2 text-red-800 font-bold">
            <span className="text-lg">🔔</span>
            <h3 className="text-sm font-black uppercase tracking-wider">Notifications & Action Alerts ({mismatchedAlerts.length})</h3>
          </div>
          <p className="text-xs text-red-700 leading-relaxed font-semibold">
            ⚠️ Attention: The master configurations for the following global catalog components have been updated by an administrator. Some of your previously configured options are no longer authorized globally. Please click 'Configure' to review and save your updates:
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 mt-3">
            {mismatchedAlerts.map(alert => (
              <div key={alert.id} className="bg-white p-3 border border-red-200 rounded-lg flex items-center justify-between shadow-sm">
                <div className="min-w-0 pr-2">
                  <p className="text-xs font-bold text-gray-800 truncate" title={alert.name}>{alert.name}</p>
                  <p className="text-[10px] text-red-500 font-semibold mt-0.5">Mismatched Options</p>
                </div>
                <button
                  onClick={() => {
                    if (setInitialComponentId) setInitialComponentId(alert.id);
                    setActiveTab('components');
                  }}
                  className="px-2.5 py-1.5 bg-red-600 hover:bg-red-700 text-white font-black text-[9px] uppercase tracking-wider rounded transition-colors"
                >
                  Configure
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="lg:col-span-2">
        <RecentActivity type="seller" setActiveTab={setActiveTab} />
      </div>
      <div className="space-y-8">
        <QuickActions setActiveTab={setActiveTab} />
        
        {mismatchedAlerts.length === 0 && (
          <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm space-y-4">
            <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
              <span>🔔</span> Notifications
            </h3>
            <p className="text-xs text-gray-500 italic">No new notifications or configuration alerts at this time.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default OverviewTab;
